/**
 * CategorizedAcknowledgments Component
 *
 * Displays required acknowledgments organized by category using an accordion pattern.
 * Implements progressive disclosure UX with mobile-first design and WCAG 2.1 AA compliance.
 *
 * Features:
 * - Categorized display with friendly labels and icons
 * - Progressive disclosure with intelligent auto-expand/collapse:
 *   * First uncompleted section starts expanded
 *   * Completed sections auto-collapse after 300ms with smooth animation
 *   * Next uncompleted section auto-expands after 150ms delay
 *   * Users can manually override by clicking accordion headers
 *   * Focus automatically moves to first unchecked item in newly expanded section
 * - Visual states:
 *   * Active section: Blue border, subtle background, pulsing icon animation
 *   * Completed section: Green border, checkmark icon with check-in animation
 *   * Pending section: Gray border, standard styling
 * - Mobile-first design with touch-optimized checkboxes (44x44px)
 * - Cost impact badges extracted from acknowledgment text
 * - Full keyboard navigation support with proper focus management
 * - Semantic HTML with proper ARIA labels and live region announcements
 * - Respects prefers-reduced-motion for all animations
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import type { RequiredAcknowledgment } from '../../types/pricing-estimation';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Truck, AlertCircle, DollarSign, Shield, FileText, CheckCircle2 } from 'lucide-react';

/**
 * Props for CategorizedAcknowledgments component
 */
export interface CategorizedAcknowledgmentsProps {
  /** List of acknowledgments with category information */
  acknowledgments: RequiredAcknowledgment[];
  /** List of acknowledgment IDs that have been accepted */
  acknowledgedItems: string[];
  /** Callback when an acknowledgment is toggled */
  onAcknowledgmentToggle: (id: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Category metadata for display
 */
interface CategoryMetadata {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

/**
 * Category configuration
 */
const CATEGORY_CONFIG: Record<string, CategoryMetadata> = {
  CONTAINER_LOADING: {
    label: 'Loading & Weight Requirements',
    icon: Truck,
    description: 'Important information about container loading and weight limits',
  },
  MATERIAL_COMPLIANCE: {
    label: 'Material & Contamination Rules',
    icon: AlertCircle,
    description: 'Rules about acceptable and prohibited materials',
  },
  FEES_PAYMENT: {
    label: 'Fees & Payment Terms',
    icon: DollarSign,
    description: 'Information about fees, pricing, and payment',
  },
  LIABILITY_TERMS: {
    label: 'Liability & Responsibility',
    icon: Shield,
    description: 'Your responsibilities and liability terms',
  },
  SERVICE_REQUIREMENTS: {
    label: 'Service Requirements',
    icon: FileText,
    description: 'Requirements for service delivery and completion',
  },
};

/**
 * Extract cost impact from acknowledgment text
 * Looks for dollar amounts in the text (e.g., "$50", "$1,234.56")
 */
function extractCostImpact(text: string): string | null {
  const match = text.match(/\$[\d,]+\.?\d*/);
  return match ? match[0] : null;
}

/**
 * Group acknowledgments by category
 */
function groupByCategory(
  acknowledgments: RequiredAcknowledgment[]
): Map<string, RequiredAcknowledgment[]> {
  const grouped = new Map<string, RequiredAcknowledgment[]>();

  for (const ack of acknowledgments) {
    const existing = grouped.get(ack.category) || [];
    grouped.set(ack.category, [...existing, ack]);
  }

  return grouped;
}

/**
 * CategorizedAcknowledgments Component
 *
 * Renders acknowledgments organized by category in an accordion pattern.
 * Implements progressive disclosure with auto-expand/collapse based on completion.
 * Only renders if there are acknowledgments to display.
 */
export function CategorizedAcknowledgments({
  acknowledgments,
  acknowledgedItems,
  onAcknowledgmentToggle,
  className = '',
}: CategorizedAcknowledgmentsProps) {
  // Group acknowledgments by category
  const groupedAcknowledgments = useMemo(
    () => groupByCategory(acknowledgments),
    [acknowledgments]
  );

  // Get ordered category list for progressive disclosure
  const orderedCategories = useMemo(() => {
    return Array.from(groupedAcknowledgments.keys());
  }, [groupedAcknowledgments]);

  // Track which sections are completed
  const completedSections = useMemo(() => {
    return new Set(
      orderedCategories.filter((category) => {
        const items = groupedAcknowledgments.get(category) || [];
        return items.every((item) => acknowledgedItems.includes(item.id));
      })
    );
  }, [orderedCategories, groupedAcknowledgments, acknowledgedItems]);

  // Find the first uncompleted section
  const firstUncompletedSection = useMemo(() => {
    return orderedCategories.find((category) => !completedSections.has(category)) || null;
  }, [orderedCategories, completedSections]);

  // Track manually controlled sections (user clicked accordion header)
  const [manuallyControlled, setManuallyControlled] = useState<Set<string>>(new Set());

  // Track expanded sections
  const [expandedSections, setExpandedSections] = useState<string[]>(() => {
    // Initially expand the first uncompleted section
    return firstUncompletedSection ? [firstUncompletedSection] : [];
  });

  // Track previous completed sections for comparison
  const prevCompletedRef = useRef<Set<string>>(new Set());

  // Ref for tracking announcement timeouts
  const announcementTimeoutRef = useRef<NodeJS.Timeout>();

  // Ref to track programmatic accordion changes (not user clicks)
  const isProgrammaticChangeRef = useRef<boolean>(false);

  // Screen reader announcements
  const [announcement, setAnnouncement] = useState<string>('');

  // Auto-collapse completed sections and auto-expand next uncompleted
  useEffect(() => {
    const newlyCompleted = Array.from(completedSections).filter(
      (section) => !prevCompletedRef.current.has(section)
    );

    if (newlyCompleted.length > 0) {
      const justCompletedSection = newlyCompleted[0];

      if (!justCompletedSection) return;

      // Only auto-manage if the section wasn't manually controlled
      if (!manuallyControlled.has(justCompletedSection)) {
        // Schedule auto-collapse after 300ms
        const collapseTimeout = setTimeout(() => {
          // Mark as programmatic change before auto-collapsing
          isProgrammaticChangeRef.current = true;
          setExpandedSections((prev) => prev.filter((s) => s !== justCompletedSection));
          // Reset flag after React processes the update
          queueMicrotask(() => { isProgrammaticChangeRef.current = false; });

          // Announce completion
          const config = CATEGORY_CONFIG[justCompletedSection];
          if (config) {
            setAnnouncement(`${config.label} section completed`);
          }

          // Schedule auto-expand of next section after additional 150ms
          const expandTimeout = setTimeout(() => {
            if (firstUncompletedSection && !manuallyControlled.has(firstUncompletedSection)) {
              // Mark as programmatic change before auto-expanding
              isProgrammaticChangeRef.current = true;
              setExpandedSections((prev) => {
                if (!prev.includes(firstUncompletedSection)) {
                  return [...prev, firstUncompletedSection];
                }
                return prev;
              });
              // Reset flag after React processes the update
              queueMicrotask(() => { isProgrammaticChangeRef.current = false; });

              // Focus first unchecked item in newly expanded section
              const nextSection = groupedAcknowledgments.get(firstUncompletedSection);
              if (nextSection) {
                const firstUnchecked = nextSection.find(
                  (item) => !acknowledgedItems.includes(item.id)
                );
                if (firstUnchecked) {
                  // Use requestAnimationFrame to ensure DOM is ready
                  requestAnimationFrame(() => {
                    const checkbox = document.getElementById(`ack-${firstUnchecked.id}`);
                    checkbox?.focus();
                  });
                }
              }

              // Announce next section
              const nextConfig = CATEGORY_CONFIG[firstUncompletedSection];
              if (nextConfig) {
                setAnnouncement(`${nextConfig.label} section opened`);
              }
            }
          }, 150);

          return () => clearTimeout(expandTimeout);
        }, 300);

        return () => clearTimeout(collapseTimeout);
      }
    }

    prevCompletedRef.current = completedSections;
  }, [completedSections, firstUncompletedSection, manuallyControlled, groupedAcknowledgments, acknowledgedItems]);

  // Clear announcement after it's read
  useEffect(() => {
    if (announcement) {
      announcementTimeoutRef.current = setTimeout(() => {
        setAnnouncement('');
      }, 1000);
    }
    return () => {
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
      }
    };
  }, [announcement]);

  // Don't render if no acknowledgments
  if (acknowledgments.length === 0) {
    return null;
  }

  // Handle accordion value changes (user manual control)
  const handleAccordionChange = (values: string[]) => {
    setExpandedSections(values);

    // Only mark as manually controlled if this is a user click (not programmatic)
    if (!isProgrammaticChangeRef.current) {
      // Mark all newly expanded sections as manually controlled
      values.forEach((value) => {
        setManuallyControlled((prev) => new Set(prev).add(value));
      });
    }
  };

  // Get step number for a category
  const getStepNumber = (category: string) => {
    return orderedCategories.indexOf(category) + 1;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <h3 className="text-lg font-medium text-gray-900">
        Required Acknowledgments
      </h3>

      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      <Accordion
        type="multiple"
        value={expandedSections}
        onValueChange={handleAccordionChange}
        className="space-y-2"
      >
        {Array.from(groupedAcknowledgments.entries()).map(([category, items]) => {
          const config = CATEGORY_CONFIG[category];
          if (!config) return null;

          const Icon = config.icon;
          const categoryAcknowledged = completedSections.has(category);
          const isActive = category === firstUncompletedSection;
          const isExpanded = expandedSections.includes(category);
          const stepNumber = getStepNumber(category);
          const totalSteps = orderedCategories.length;

          // Count acknowledged items in this category
          const acknowledgedCount = items.filter((item) =>
            acknowledgedItems.includes(item.id)
          ).length;

          return (
            <AccordionItem
              key={category}
              value={category}
              className={`
                border-2 rounded-lg bg-white shadow-sm overflow-hidden transition-all duration-200
                ${categoryAcknowledged
                  ? 'border-accent-500'
                  : isActive && isExpanded
                  ? 'border-primary-500 bg-primary-50/30'
                  : 'border-gray-200'
                }
              `}
              aria-current={isActive ? 'step' : undefined}
            >
              <AccordionTrigger
                className="px-4 py-4 hover:bg-gray-50 transition-colors"
                aria-label={`Step ${stepNumber} of ${totalSteps}: ${config.label}. ${
                  categoryAcknowledged
                    ? 'Completed'
                    : `${acknowledgedCount} of ${items.length} acknowledged`
                }`}
              >
                <div className="flex items-center gap-3 flex-1 text-left">
                  <div
                    className={`
                      p-2 rounded-lg transition-all duration-200
                      ${categoryAcknowledged
                        ? 'bg-accent-100'
                        : isActive
                        ? 'bg-primary-100'
                        : 'bg-gray-100'
                      }
                      ${isActive && isExpanded && !categoryAcknowledged
                        ? 'motion-safe:animate-focus-pulse'
                        : ''
                      }
                    `}
                  >
                    {categoryAcknowledged ? (
                      <CheckCircle2
                        className="h-5 w-5 text-accent-600 motion-safe:animate-check-in"
                        aria-hidden="true"
                      />
                    ) : (
                      <Icon
                        className={`
                          h-5 w-5 transition-colors
                          ${isActive ? 'text-primary-600' : 'text-gray-600'}
                        `}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">
                        {config.label}
                      </span>
                      <Badge
                        variant={categoryAcknowledged ? 'success' : 'secondary'}
                        className="text-xs"
                      >
                        {categoryAcknowledged
                          ? 'Complete'
                          : `${acknowledgedCount}/${items.length}`}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {config.description}
                    </p>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3" role="group" aria-label={config.label}>
                  {items.map((ack) => {
                    const isChecked = acknowledgedItems.includes(ack.id);
                    const costImpact = extractCostImpact(ack.text);
                    const checkboxId = `ack-${ack.id}`;

                    return (
                      <label
                        key={ack.id}
                        htmlFor={checkboxId}
                        className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 min-h-[44px]"
                      >
                        {/* Touch-optimized checkbox */}
                        <div className="flex items-center justify-center min-w-[44px] min-h-[44px]">
                          <input
                            id={checkboxId}
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => onAcknowledgmentToggle(ack.id)}
                            className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                            aria-required={ack.required}
                          />
                        </div>

                        {/* Acknowledgment text and badges */}
                        <div className="flex-1 min-w-0 pt-2.5">
                          <div className="flex items-start gap-2 flex-wrap">
                            <span className="text-sm text-gray-700 flex-1">
                              {ack.text}
                            </span>
                            {costImpact && (
                              <Badge variant="warning" className="text-xs flex-shrink-0">
                                {costImpact}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Helper text */}
      <p className="text-xs text-gray-500 mt-3">
        All required acknowledgments must be accepted before proceeding to checkout.
      </p>
    </div>
  );
}
